import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { buildAlbumParam } from "@/lib/slugify";
import { useServerFn } from "@tanstack/react-start";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, memo, useEffect, useRef, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  Loader2,
  Heart,
  MessageCircle,
  Disc3,
  Tag,
  Repeat2,
  Send,
  Sparkles,
  Users,
  Music2,
  Mic2,
  MoreHorizontal,
  Pencil,
  Trash2,
  ScanLine,
  ArrowRight,
  LayoutGrid,
  List,
} from "lucide-react";

import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { z } from "zod";

import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { CommunityArtistsStrip } from "@/components/artists/CommunityArtistsStrip";
import { Linkify } from "@/components/Linkify";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { ShiningText } from "@/components/ui/shining-text";
import { thumbUrl } from "@/lib/image-thumb";
import { BorderBeam } from "border-beam";
import { searchArtists } from "@/lib/artists.functions";
import { CommentsPanel } from "@/components/feed/PostCommentsPanel";
import { lazy, Suspense } from "react";
// Perf: o painel de detalhe do post só carrega quando alguém abre um post.
const PostDetailDrawer = lazy(() =>
  import("@/components/feed/PostDetailDrawer").then((m) => ({
    default: m.PostDetailDrawer,
  })),
);

const FeedGrid = lazy(() =>
  import("@/components/community/FeedGrid").then((m) => ({ default: m.FeedGrid })),
);
import { FeedTabSkeleton } from "@/components/community/FeedSkeletons";
const PostComposerDialog = lazy(() =>
  import("@/components/community/PostComposer").then((m) => ({ default: m.PostComposerDialog })),
);
const FloatingComposer = lazy(() =>
  import("@/components/community/FloatingComposer").then((m) => ({ default: m.FloatingComposer })),
);

const ScanBarcodeDialog = lazy(() =>
  import("@/components/ScanBarcodeDialog").then((m) => ({ default: m.ScanBarcodeDialog })),
);
import { AlbumCoverSkeleton } from "@/components/AlbumCoverSkeleton";
import { ProBadge } from "@/components/ui/pro-badge";
import { FramedAvatar } from "@/components/FramedAvatar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Drawer as DrawerPrimitive } from "vaul";
import { useIsMobile } from "@/hooks/use-mobile";
import { SegmentedTabs } from "@/components/ui/segmented-tabs";
import { SubNavBar } from "@/components/ui/sub-nav";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
const GifPicker = lazy(() =>
  import("@/components/ui/gif-picker").then((m) => ({ default: m.GifPicker })),
);
import { toast } from "sonner";
import { addAlbum, getMyAlbumByDiscogs, removeAlbum } from "@/lib/collection.functions";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useIsPremium } from "@/hooks/useIsPremium";
import { openProDrawer } from "@/components/ProDrawer";
import {
  searchDiscogsPaginated,
  type DiscogsSearchItem,
} from "@/lib/discogs.functions";

import {
  listFollowingFeed,
  listSpotlightFeed,
  listWishlistInterestFeed,
  listRecentCollectionFeed,
  listRecentCollectionFeedPublic,
  togglePostLike,
  listPostComments,
  createComment,
  updatePostComment,
  deletePostComment,
  type FeedRow,
  type FeedPage,
} from "@/lib/feed.functions";

import { cn } from "@/lib/utils";
import { UpvoteButton } from "@/components/ratings/UpvoteButton";
import { ScorePill } from "@/components/ratings/ScorePill";
import { CollectorsStack } from "@/components/ratings/CollectorsStack";
import { useAlbumCommunityMeta } from "@/hooks/useAlbumCommunityMeta";
import type { CollectorAvatar } from "@/lib/ratings.functions";
import { getArtistImages } from "@/lib/artist-images.functions";

type Tab = "for-you" | "spotlight" | "discover" | "interest" | "feed";

const tabSchema = z.object({
  tab: z.enum(["for-you", "spotlight", "discover", "interest", "feed"]).optional(),
  post: z.string().optional(),
});

export const Route = createFileRoute("/explore")({
  validateSearch: tabSchema,
  head: () => ({
    meta: [
      { title: "Explorar — Viny Club" },
      {
        name: "description",
        content:
          "Feed da comunidade, destaques da plataforma e descoberta de novos discos — tudo em um só lugar.",
      },
      { property: "og:title", content: "Explorar — Viny Club" },
      { property: "og:description", content: "Compartilhe discos, opiniões e descobertas com a comunidade do Viny Club." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ExplorePage,
});

function ExplorePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/explore" });
  const requestedTab: Tab = (search.tab as Tab) ?? "discover";
  const tab: Tab = requestedTab === "interest" ? "discover" : requestedTab;
  const setTab = (t: Tab) =>
    navigate({ search: () => ({ tab: t === "discover" ? undefined : t }), replace: true });
  const openPostId = (search.post as string | undefined) ?? null;
  const setOpenPost = (id: string | null) =>
    navigate({
      search: (prev: any) => ({ ...prev, post: id ?? undefined }),
      replace: true,
    });

  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isMac, setIsMac] = useState(true);
  const [scanOpen, setScanOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMenuOpen, setComposerMenuOpen] = useState(false);
  const [composerMode, setComposerMode] = useState<"text" | "media" | "album">("text");
  const [composerAlbum, setComposerAlbum] = useState<import("@/lib/community-feed.functions").CommunityAlbumRef | null>(null);


  useEffect(() => {
    if (typeof navigator !== "undefined") {
      setIsMac(/Mac|iPhone|iPad|iPod/.test(navigator.platform));
    }
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const raw = window.sessionStorage.getItem("viny:share-album");
    if (!raw) return;
    window.sessionStorage.removeItem("viny:share-album");
    try {
      setComposerAlbum(JSON.parse(raw));
      setComposerMode("album");
      setComposerOpen(true);
    } catch {
      setComposerAlbum(null);
    }
  }, []);

  const { profile, user } = useCurrentUser();
  const firstName =
    (profile?.display_name || profile?.username || "Colecionador").split(" ")[0] ??
    "Colecionador";

  const submit = (v?: string) => {

    const val = (v ?? query).trim();
    setQuery(val);
    setSubmitted(val);
  };


  return (
    <AppShell>
      <SubNavBar className="mb-4 lg:hidden">
        <SegmentedTabs<Tab>
          layoutId="explore-tabs-mobile"
          ariaLabel="Seções do explorar"
          fullWidth
          size="sm"
          className="h-11 rounded-2xl border-foreground/10 bg-surface/70 p-1"
          items={[
            { value: "discover", label: "Discos recentes" },
            { value: "feed", label: "Feed" },
          ]}
          value={tab}
          onChange={setTab}
        />
      </SubNavBar>

      <div className="mx-auto w-full max-w-[1400px] px-4 lg:px-5">
        <ExploreHero
          query={query}
          setQuery={setQuery}
          submit={submit}
          inputRef={inputRef}
          onScan={() => setScanOpen(true)}
          firstName={firstName}
          canScan={!!user}
          feedMode={tab === "feed"}
          onCompose={() => setComposerOpen(true)}
        />
      </div>

      <Suspense fallback={null}>
        {composerOpen && (
          <PostComposerDialog open={composerOpen} onOpenChange={setComposerOpen} initialMode={composerMode} initialAlbum={composerAlbum} />
        )}
      </Suspense>
      {tab === "feed" && !openPostId && !composerOpen && (
        <Suspense fallback={null}>
          <FloatingComposer
            open={composerMenuOpen}
            onOpenChange={setComposerMenuOpen}
            onSelect={(mode) => {
              setComposerMode(mode);
              setComposerAlbum(null);
              setComposerMenuOpen(false);
              setComposerOpen(true);
            }}
          />
        </Suspense>
      )}

      <div className="mx-auto w-full max-w-[1400px] px-4 pb-16 pt-5 lg:px-5">
        {tab === "discover" && (
          <DiscoverTab
            submitted={submitted}
            initialSection={requestedTab === "interest" ? "interest" : "added"}
            onClearSearch={() => {
              setQuery("");
              setSubmitted("");
            }}
          />
        )}
        {tab === "for-you" && <ForYouFeed />}
        {tab === "feed" && (
          <Suspense fallback={<FeedTabSkeleton />}>
            <FeedGrid openPostId={openPostId} onOpenPost={setOpenPost} hideComposer />
          </Suspense>
        )}
      </div>

      {scanOpen && (
        <Suspense fallback={null}>
          <ScanBarcodeDialog open={scanOpen} onOpenChange={setScanOpen} />
        </Suspense>
      )}
    </AppShell>
  );
}

// ─────────────────────────────────────────────────────── Hero card ───
function ExploreHero({
  query,
  setQuery,
  submit,
  inputRef,
  onScan,
  firstName,
  canScan,
  feedMode,
  onCompose,
}: {
  query: string;
  setQuery: (v: string) => void;
  submit: (v?: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onScan: () => void;
  firstName: string;
  canScan: boolean;
  feedMode?: boolean;
  onCompose?: () => void;
}) {
  return (
    <div className="relative isolate mt-6 overflow-hidden rounded-[var(--radius-2xl)] bg-[#1A1A1A] lg:mt-8">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(320px,42%)]">
        {/* Conteúdo: saudação + busca */}
        <div className="relative z-10 p-6 py-10 lg:p-10 lg:py-12">
          <div className="max-w-xl">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/55">
              Explorar
            </p>
            <h1 className="mt-2 font-display text-3xl font-medium tracking-[-0.03em] text-white lg:hidden">
              Olá, {firstName} 💜
            </h1>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-white/60 lg:mt-4 lg:text-[15px]">
              {feedMode
                ? "Compartilhe o que está girando: um pensamento, uma foto, um vídeo ou um disco."
                : "Explore coleções, descubra discos novos e conecte-se com amantes de vinil."}
            </p>

            {!feedMode ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submit();
                }}
                className="mt-6 w-full"
              >
                <BorderBeam
                  size="md"
                  colorVariant="colorful"
                  strength={0.7}
                  theme="dark"
                  borderRadius={12}
                >
                <div className="group relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/55 transition-colors group-focus-within:text-white" />
                  <input
                    id="explore-search-input"
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        submit();
                      }
                    }}
                    placeholder="Busque artistas, álbuns ou gêneros…"
                    className={cn(
                      "h-14 w-full rounded-xl border border-white/15 bg-black/25 pl-12 pr-24 text-[15px] text-white shadow-sm transition-all placeholder:text-white/55 focus:border-white/40 focus:bg-black/35 focus:outline-none",
                      query.length > 0 && "bg-black/35"
                    )}
                  />
                  <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-2">
                    {canScan && (
                      <button
                        type="button"
                        aria-label="Escanear código de barras"
                        onClick={onScan}
                        className="relative grid h-9 w-9 place-items-center rounded-lg border border-white/15 bg-black/30 text-white transition-transform hover:scale-[1.03] hover:bg-black/45"
                      >
                        <ScanLine className="h-4 w-4" />
                      </button>
                    )}

                    <button
                      type="submit"
                      aria-label="Buscar"
                      className="grid h-9 w-9 place-items-center rounded-lg bg-white text-neutral-950 transition-transform hover:scale-[1.03]"
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                </BorderBeam>
              </form>
            ) : null}

          </div>
        </div>

        {/* Marquee de artistas — desktop apenas */}
        <div className="relative hidden overflow-hidden lg:block">
          <div className="pointer-events-none absolute inset-0 z-0">
            <ArtistMarquee />
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 z-[2] w-32 bg-gradient-to-r from-surface-elevated via-surface-elevated/80 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-[2] w-24 bg-gradient-to-l from-surface-elevated/90 to-transparent" />
        </div>
      </div>
    </div>
  );
}

const MARQUEE_ARTISTS = [
  "Tim Maia", "Rita Lee", "Djavan", "Gilberto Gil", "Gal Costa", "Milton Nascimento",
  "Jorge Ben Jor", "Elis Regina", "Caetano Veloso", "Marisa Monte", "Racionais MC's",
  "Liniker", "Chico Buarque", "Novos Baianos", "Nação Zumbi", "Cartola", "Cássia Eller",
  "Seu Jorge",
];

function ArtistMarquee() {
  const isMobile = useIsMobile();
  const getImages = useServerFn(getArtistImages);
  const imagesQuery = useQuery({
    queryKey: ["explore-artist-marquee-images"],
    queryFn: () => getImages({ data: { artists: MARQUEE_ARTISTS } }),
    enabled: !isMobile,
    staleTime: 1000 * 60 * 60 * 24,
  });
  if (isMobile) return null;
  const rows = 2;
  const perRow = Math.ceil(MARQUEE_ARTISTS.length / rows);

  return (
    <div className="artist-marquee-mask flex h-full w-full flex-col justify-center gap-4 overflow-hidden py-7">
      {Array.from({ length: rows }).map((_, i) => {
        const items = Array.from({ length: perRow }, (_, j) =>
          MARQUEE_ARTISTS[(i * perRow + j) % MARQUEE_ARTISTS.length]
        );
        return (
          <div
            key={i}
            className={i === 1 ? "artist-marquee-track artist-marquee-track-reverse" : "artist-marquee-track"}
          >
            {[0, 1].map((copy) => (
              <div key={copy} className="flex shrink-0 gap-4 pr-4" aria-hidden={copy === 1}>
                {items.map((artist) => {
                  const image = imagesQuery.data?.[artist];
                  return (
                    <span
                      key={`${copy}-${artist}`}
                      className="group relative inline-flex h-[92px] w-[92px] shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-black/35 shadow-[0_12px_30px_rgba(0,0,0,0.28)] ring-1 ring-white/[0.04]"
                      title={artist}
                    >
                      {image ? (
                        <img
                          src={thumbUrl(image, 192)}
                          alt={copy === 0 ? artist : ""}
                          width={92}
                          height={92}
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover opacity-90 transition-[opacity,transform] duration-500 group-hover:scale-105 group-hover:opacity-100"
                        />
                      ) : (
                        <span className="grid h-full w-full place-items-center text-lg font-bold text-white/55">
                          {artist.slice(0, 1)}
                        </span>
                      )}
                      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 pb-1.5 pt-5 text-center text-[10px] font-medium text-white/90 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        {artist}
                      </span>
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}




// ─────────────────────────────────────────────────────── Quick add (+) ───
type AddTarget = { discogsId: string; title: string; artist: string; year: number | null; cover: string | null };

function QuickAddButton({
  album,
  size = "sm",
  variant = "icon",
}: {
  album: AddTarget;
  size?: "sm" | "md";
  variant?: "icon" | "full";
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const { isAuthenticated } = useCurrentUser();
  const addFn = useServerFn(addAlbum);
  const removeFn = useServerFn(removeAlbum);
  const getMineFn = useServerFn(getMyAlbumByDiscogs);
  const qc = useQueryClient();

  const mineQ = useQuery({
    queryKey: ["my-album-by-discogs", album.discogsId],
    enabled: isAuthenticated && open,
    queryFn: () => getMineFn({ data: { discogsId: album.discogsId } }),
    staleTime: 30_000,
  });
  const mine = mineQ.data;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["collection"] });
    qc.invalidateQueries({ queryKey: ["wishlist"] });
    qc.invalidateQueries({ queryKey: ["my-album-by-discogs", album.discogsId] });
  };

  const add = useMutation({
    mutationFn: (kind: "owned" | "wishlist") =>
      addFn({
        data: {
          discogsId: album.discogsId,
          status: kind,
          listing: "none",
          title: album.title,
          artist: album.artist,
          year: album.year,
          cover: album.cover ?? "",
        },
      }),
    onSuccess: (_d, kind) => {
      toast.success(kind === "owned" ? "Adicionado à coleção" : "Salvo no interesse");
      invalidate();
      setOpen(false);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Erro ao adicionar";
      if (msg.includes("Unauthorized")) toast.error("Faça login para adicionar");
      else toast.error(msg);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeFn({ data: { id } }),
    onSuccess: () => {
      toast.success(mine?.status === "owned" ? "Removido da coleção" : "Removido do interesse");
      invalidate();
      setOpen(false);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Erro ao remover";
      toast.error(msg);
    },
  });

  const busy = add.isPending || remove.isPending;
  const dim = size === "md" ? "h-9 w-9" : "h-7 w-7";

  const stop = {
    onClick: (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
  };

  const trigger =
    variant === "full" ? (
      <button
        type="button"
        aria-label="Adicionar"
        {...stop}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-full border border-border bg-surface px-2 py-1 text-[11px] font-semibold hover:border-primary/50"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Adicionar
      </button>
    ) : (
      <button
        type="button"
        aria-label="Adicionar"
        data-quick-add-open={open ? "true" : "false"}
        {...stop}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className={cn(
          "grid shrink-0 place-items-center rounded-full border border-border/70 bg-background/90 text-foreground shadow-sm backdrop-blur transition-colors hover:border-foreground/40 hover:bg-foreground hover:text-background",
          dim,
        )}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
      </button>
    );

  const isOwned = mine?.status === "owned";
  const isWishlist = mine?.status === "wishlist";

  const options = mineQ.isLoading ? (
    <div className="flex items-center justify-center px-3 py-6 text-xs text-muted-foreground">
      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Verificando...
    </div>
  ) : isOwned || isWishlist ? (
    <>
      <div className="flex items-start gap-3 rounded-lg px-3 py-3">
        {isOwned ? (
          <Disc3 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        ) : (
          <Heart className="mt-0.5 h-5 w-5 shrink-0 text-rose-400" />
        )}
        <div className="min-w-0 text-sm">
          <div className="font-medium">
            {isOwned ? "Já está na sua coleção" : "Já está no seu interesse"}
          </div>
          <div className="text-[11px] text-muted-foreground">
            Você já adicionou esse disco.
          </div>
        </div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => mine && remove.mutate(mine.id)}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-medium text-rose-400 hover:bg-surface"
      >
        <Trash2 className="h-5 w-5" />
        <div className="min-w-0">
          <div className="truncate">
            {isOwned ? "Remover da coleção" : "Remover do interesse"}
          </div>
          <div className="truncate text-[11px] font-normal text-muted-foreground">
            Você pode adicionar de novo depois
          </div>
        </div>
      </button>
    </>
  ) : (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={() => add.mutate("owned")}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-medium hover:bg-surface"
      >
        <Disc3 className="h-5 w-5 text-primary" />
        <div className="min-w-0">
          <div className="truncate">Adicionar à coleção</div>
          <div className="truncate text-[11px] font-normal text-muted-foreground">Eu tenho esse disco</div>
        </div>
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => add.mutate("wishlist")}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-medium hover:bg-surface"
      >
        <Heart className="h-5 w-5 text-rose-400" />
        <div className="min-w-0">
          <div className="truncate">Salvar no interesse</div>
          <div className="truncate text-[11px] font-normal text-muted-foreground">Quero conseguir um</div>
        </div>
      </button>
    </>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <DrawerPrimitive.Root open={open} onOpenChange={setOpen}>
          <DrawerPrimitive.Portal>
            <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
            <DrawerPrimitive.Content
              onClick={(e) => e.stopPropagation()}
              className="fixed inset-x-0 bottom-0 z-50 flex flex-col gap-1 rounded-t-2xl border border-border/60 bg-card p-2 pb-[calc(env(safe-area-inset-bottom)+12px)] text-foreground shadow-2xl outline-none"
            >
              <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-border" />
              <DrawerPrimitive.Title className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Adicionar disco
              </DrawerPrimitive.Title>
              <DrawerPrimitive.Description className="sr-only">
                Escolha onde adicionar {album.title}
              </DrawerPrimitive.Description>
              <div className="mt-1 flex flex-col">{options}</div>
            </DrawerPrimitive.Content>
          </DrawerPrimitive.Portal>
        </DrawerPrimitive.Root>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-56 rounded-xl border-border/60 bg-card/95 p-1.5 shadow-xl backdrop-blur"
        onClick={(e) => e.stopPropagation()}
      >
        {options}
      </PopoverContent>
    </Popover>
  );
}


// ─────────────────────────────────────────────────────── Shared card ───
function ExploreCard({
  to,
  toParams,
  cover,
  title,
  artist,
  userId,
  userName,
  userAvatar,
  userHref,
  isPremium,
  likeCount,
  liked,
  onToggleLike,
  commentCount,
  onComment,
  addAlbumData,
  anchorId,
  onOpen,
  score,
  raters,
  collectors,
  collectorsCount,
}: {
  to: "/album/$id";
  toParams: { id: string };
  cover: string | null;
  title: string;
  artist: string;
  userId?: string | null;
  userName: string;
  userAvatar: string | null;
  userHref?: { username: string };
  ratio?: string;
  isPremium?: boolean;
  likeCount?: number;
  liked?: boolean;
  onToggleLike?: () => void;
  commentCount?: number;
  onComment?: () => void;
  addAlbumData?: AddTarget;
  anchorId?: string;
  /** Quando definido, clicar na capa/título abre o post em modal em vez de navegar. */
  onOpen?: () => void;
  score?: number | null;
  raters?: number;
  collectors?: CollectorAvatar[];
  collectorsCount?: number;
}) {
  const coverBody = (
    <div className="relative aspect-square w-full overflow-hidden rounded-lg">
      {cover ? (
        <img
          src={cover}
          alt={`${title} — ${artist}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
      ) : (
        <AlbumCoverSkeleton showMeta={false} />
      )}
      {/* Gradiente sutil para legibilidade dos overlays */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
    </div>
  );

  return (
    <div id={anchorId} className="group relative flex h-full w-full flex-col scroll-mt-24 rounded-xl border border-border/40 bg-[#1A1A1A] p-2.5 transition-colors hover:border-border has-[[data-quick-add-open=true]]:border-border target:ring-2 target:ring-primary/60">
      {/* Capa com ações e nota em overlay */}
      <div className="relative w-full overflow-hidden rounded-lg bg-muted/40">
        {onOpen ? (
          <button type="button" onClick={onOpen} className="relative block w-full text-left">
            {coverBody}
          </button>
        ) : (
          <Link to={to} params={toParams} className="relative block w-full">
            {coverBody}
          </Link>
        )}

        {/* Nota da comunidade — overlay topo-esquerdo */}
        {score != null && (
          <div className="absolute left-2 top-2 z-10">
            <ScorePill
              score={score}
              raters={raters}
              className="border-amber-400/30 bg-black/80 px-2 py-1 text-[12px] font-bold text-amber-300 shadow-lg"
            />
          </div>
        )}

        {/* Quick add — overlay topo-direito (hover / touch) */}
        {addAlbumData && (
          <div className="absolute right-2 top-2 z-20 opacity-0 transition-opacity duration-200 group-hover:opacity-100 has-[[data-quick-add-open=true]]:opacity-100 [@media(hover:none)]:opacity-100">
            <QuickAddButton album={addAlbumData} />
          </div>
        )}
      </div>

      {/* Autor — linha fina logo abaixo da capa */}
      <div className="mt-2 flex items-center gap-1.5">
        {userHref ? (
          <Link
            to="/profile/$username"
            params={userHref}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <img loading="lazy" decoding="async"
              src={userAvatar || `https://api.dicebear.com/7.x/initials/svg?seed=${userHref?.username ?? userName}`}
              alt=""
              className="h-5 w-5 rounded-full bg-muted object-cover"
              style={isPremium ? { border: "1.5px solid #9231FF", boxSizing: "border-box" } : undefined}
            />
            <span className="truncate">{userName}</span>
            {isPremium && <ProBadge size="xs" aria-label="Premium" />}
          </Link>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px] text-muted-foreground">
            <img loading="lazy" decoding="async"
              src={userAvatar || `https://api.dicebear.com/7.x/initials/svg?seed=${userName}`}
              alt=""
              className="h-5 w-5 rounded-full bg-muted object-cover"
              style={isPremium ? { border: "1.5px solid #9231FF", boxSizing: "border-box" } : undefined}
            />
            <span className="truncate">{userName}</span>
            {isPremium && <ProBadge size="xs" aria-label="Premium" />}
          </div>
        )}
      </div>

      {/* Identidade do álbum */}
      {onOpen ? (
        <button type="button" onClick={onOpen} className="mt-1.5 block w-full text-left">
          <div className="line-clamp-2 text-[13px] font-semibold leading-snug text-foreground">
            {title}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{artist}</div>
        </button>
      ) : (
        <Link to={to} params={toParams} className="mt-1.5 block">
          <div className="line-clamp-2 text-[13px] font-semibold leading-snug text-foreground">
            {title}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{artist}</div>
        </Link>
      )}

      {/* Footer — comunidade + engajamento */}
      {(onToggleLike || onComment || (collectorsCount ?? 0) > 0) && (
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <CollectorsStack
            people={collectors ?? []}
            count={collectorsCount ?? 0}
            size={16}
            className="min-w-0 flex-1"
            labelClassName="hidden sm:inline"
          />
          <div className="flex shrink-0 items-center gap-1.5">
            {onComment && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onComment();
                }}
                aria-label="Comentar"
                className="flex h-7 items-center gap-1 rounded-full border border-border/50 bg-surface/60 px-2 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
              >
                <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.8} />
                {commentCount != null && commentCount > 0 && (
                  <span className="tabular-nums">{commentCount}</span>
                )}
              </button>
            )}
            {onToggleLike && (
              <UpvoteButton
                size="sm"
                count={likeCount ?? 0}
                active={liked}
                onClick={onToggleLike}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const RATIOS = ["1 / 1", "3 / 4", "1 / 1", "4 / 5", "1 / 1", "3 / 4"];


function FeedPostsGrid({
  posts,
  queryKey,
  view = "grid",
}: {
  posts: FeedRow[];
  queryKey: string;
  view?: "grid" | "list";
}) {
  const qc = useQueryClient();
  const likeFn = useServerFn(togglePostLike);
  const [commentPost, setCommentPost] = useState<FeedRow | null>(null);
  const [openPostId, setOpenPostId] = useState<string | null>(null);
  const { isAuthenticated } = useCurrentUser();
  const withAlbum = posts.filter((p) => p.album?.cover_url);
  const getMeta = useAlbumCommunityMeta(withAlbum.map((p) => p.album?.discogs_id));
  if (withAlbum.length === 0) return null;


  const toggle = (postId: string) => {
    if (!isAuthenticated) {
      toast.error("Entre para recomendar");
      return;
    }
    const mapPost = (p: FeedRow) =>
      p.id === postId
        ? {
            ...p,
            liked_by_me: !p.liked_by_me,
            likes_count: p.likes_count + (p.liked_by_me ? -1 : 1),
          }
        : p;
    // Suporta tanto listas simples quanto o formato de infinite query (pages).
    qc.setQueriesData({ queryKey: [queryKey] }, (old: unknown) => {
      if (!old) return old;
      if (Array.isArray(old)) return (old as FeedRow[]).map(mapPost);
      const paged = old as { pages?: (FeedRow[] | { rows: FeedRow[] })[] };
      if (Array.isArray(paged.pages)) {
        return {
          ...paged,
          pages: paged.pages.map((pg) =>
            Array.isArray(pg) ? pg.map(mapPost) : { ...pg, rows: pg.rows.map(mapPost) },
          ),
        };
      }
      return old;
    });
    likeFn({ data: { postId } }).catch(() => {
      qc.invalidateQueries({ queryKey: [queryKey] });
    });
  };


  return (
    <>
      <div
        className={cn(
          "animate-fade-in",
          view === "grid"
            ? "grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
            : "flex flex-col gap-1",
        )}
      >
        {withAlbum.map((post, i) => {
          const meta = getMeta(post.album!.discogs_id);
          if (view === "list") {
            return (
              <div
                key={post.id}
                className="flex min-w-0 items-center gap-3 rounded-md px-1 py-1.5 transition-colors hover:bg-surface/60"
              >
                <Link
                  to="/album/$id"
                  params={{ id: buildAlbumParam(post.album!.discogs_id ?? post.album!.id, post.album!.artist, post.album!.title) }}
                  className="flex min-w-0 flex-1 items-center gap-3"
                >
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-muted ring-1 ring-border/60">
                    <img
                      src={post.album!.cover_url ?? ""}
                      alt={post.album!.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{post.album!.title}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {post.album!.artist}{post.album!.year ? ` · ${post.album!.year}` : ""}
                    </div>
                  </div>
                </Link>
                <Link
                  to="/profile/$username"
                  params={{ username: post.author.username }}
                  className="hidden min-w-0 items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground sm:flex"
                >
                  <img
                    src={post.author.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${post.author.username}`}
                    alt=""
                    loading="lazy"
                    className="h-6 w-6 rounded-full object-cover"
                  />
                  <span className="max-w-28 truncate">{post.author.display_name ?? post.author.username}</span>
                </Link>
                {post.album!.discogs_id && (
                  <QuickAddButton
                    album={{
                      discogsId: post.album!.discogs_id,
                      title: post.album!.title,
                      artist: post.album!.artist,
                      year: post.album!.year,
                      cover: post.album!.cover_url,
                    }}
                  />
                )}
              </div>
            );
          }
          return (
          <Fragment key={post.id}>
            <ExploreCard

            to="/album/$id"
            toParams={{ id: buildAlbumParam(post.album!.discogs_id ?? post.album!.id, post.album!.artist, post.album!.title) }}
            cover={post.album!.cover_url ?? null}
            title={post.album!.title}
            artist={post.album!.artist}
            userName={post.author.display_name ?? post.author.username}
            userId={post.author.id}
            userAvatar={post.author.avatar_url ?? null}
            userHref={{ username: post.author.username }}
            isPremium={post.author.is_premium}
            likeCount={post.likes_count}
            liked={post.liked_by_me}
            onToggleLike={() => toggle(post.id)}
            commentCount={post.comments_count}
            onComment={() => setOpenPostId(post.id)}
            onOpen={() => setOpenPostId(post.id)}
            score={meta.score}
            raters={meta.raters}
            collectors={meta.collectors}
            collectorsCount={meta.collectorsCount}

            addAlbumData={
              post.album!.discogs_id
                ? {
                    discogsId: post.album!.discogs_id,
                    title: post.album!.title,
                    artist: post.album!.artist,
                    year: post.album!.year,
                    cover: post.album!.cover_url,
                  }
                : undefined
            }
            />
          </Fragment>
          );
        })}


      </div>
      {commentPost && (
        <CommentsDrawer
          open={!!commentPost}
          onOpenChange={(v) => !v && setCommentPost(null)}
          post={commentPost}
        />
      )}
      {openPostId && (
        <Suspense fallback={null}>
          <PostDetailDrawer
            posts={withAlbum}
            openId={openPostId}
            onOpenChange={setOpenPostId}
            onToggleLike={toggle}
          />
        </Suspense>
      )}
    </>
  );
}

function InterestSkeleton({ count = 10 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="space-y-3">
          <div className="aspect-square w-full animate-pulse rounded-lg bg-[#1A1A1A]" />
          <div className="h-3.5 w-2/3 animate-pulse rounded bg-[#1A1A1A]" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-[#1A1A1A]" />
        </div>
      ))}
    </div>
  );
}

// Interesse da comunidade usa exatamente o mesmo card de "Adicionados recentemente".
const InterestGrid = memo(function InterestGrid({
  posts,
  queryKey = "wishlist-interest-feed",
}: {
  posts: FeedRow[];
  queryKey?: string;
}) {
  return <FeedPostsGrid posts={posts} queryKey={queryKey} />;
});

// ───────────────────────────────── Feed paginado com scroll infinito ───
// Pro: scroll infinito de verdade. Free: carrega até MAX_FREE_PAGES e mostra o
// card de apoio no final.
const PAGE_SIZE = 18;
const MAX_FREE_PAGES = 3; // primeira carga + 2 "scrolladas"

function PaginatedFeedSection({
  queryKey,
  fetchPage,
  view = "grid",
}: {
  queryKey: string;
  fetchPage: (offset: number, limit: number) => Promise<FeedPage>;
  view?: "grid" | "list";
}) {
  const { isPremium } = useIsPremium();
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const q = useInfiniteQuery<FeedPage>({
    queryKey: [queryKey],
    queryFn: ({ pageParam }) => fetchPage(pageParam as number, PAGE_SIZE),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasMore ? last.nextOffset : undefined),
    staleTime: 60_000,
  });

  const pages = q.data?.pages ?? [];
  const reachedFreeLimit = !isPremium && pages.length >= MAX_FREE_PAGES;
  const canLoadMore = !!q.hasNextPage && !reachedFreeLimit;

  // Dedup por álbum para não repetir capas entre páginas.
  const seen = new Set<string>();
  const posts = pages
    .flatMap((pg) => pg.rows)
    .filter((p) => {
      const key = p.album?.id ?? p.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !canLoadMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !q.isFetchingNextPage) q.fetchNextPage();
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [canLoadMore, q.isFetchingNextPage, q.fetchNextPage]);

  if (q.isLoading) return <SkeletonGrid />;
  if (posts.length === 0)
    return <p className="text-sm text-muted-foreground">Nada por aqui ainda.</p>;

  return (
    <>
      <FeedPostsGrid posts={posts} queryKey={queryKey} view={view} />

      {canLoadMore && (
        <div ref={sentinelRef} className="pt-8">
          {q.isFetchingNextPage && (
            <div className="grid place-items-center py-2">
              <ShiningText text="Carregando" />
            </div>
          )}
        </div>
      )}

      {reachedFreeLimit && <KeepScrollingCard />}
    </>
  );
}

function KeepScrollingCard() {
  return (
    <div className="relative pt-6 sm:pt-10">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-32 h-32 bg-gradient-to-b from-transparent to-background"
      />
      <div className="relative grid gap-6 overflow-hidden rounded-[var(--radius-2xl)] bg-[#1A1A1A] p-6 text-white sm:grid-cols-[minmax(0,1fr)_minmax(280px,0.8fr)] sm:items-center sm:p-8 lg:p-10">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase text-white/55">Viny Pro</p>
          <h3 className="mt-2 max-w-[15ch] font-display text-3xl font-medium leading-tight sm:text-4xl">
            Continue explorando sem limites
          </h3>
        </div>

        <div className="min-w-0 sm:justify-self-end">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <p className="max-w-sm text-sm leading-relaxed text-white/70 sm:text-[15px]">
              Desbloqueie o scroll infinito e descubra todos os discos adicionados pela comunidade.
            </p>
          </div>
          <Button
          type="button"
          onClick={() => openProDrawer()}
            size="lg"
            className="mt-5 h-12 w-full rounded-full px-6 font-bold sm:w-auto"
          >
            Assinar o Pro
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}


function ForYouGrid({ posts }: { posts: FeedRow[] }) {
  const qc = useQueryClient();
  const likeFn = useServerFn(togglePostLike);
  const [openPostId, setOpenPostId] = useState<string | null>(null);
  const withAlbum = posts.filter((p) => p.album?.cover_url);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.location.hash.match(/^#post-(.+)$/);
    if (!m) return;
    const target = withAlbum.find((p) => p.id === m[1]);
    if (!target) return;
    const el = document.getElementById(`post-${target.id}`);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setOpenPostId(target.id);
      }, 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withAlbum.length]);

  if (withAlbum.length === 0) return null;

  const toggle = (postId: string) => {
    qc.setQueriesData<FeedRow[]>({ queryKey: ["following-feed"] }, (old) =>
      (old ?? []).map((p) =>
        p.id === postId
          ? {
              ...p,
              liked_by_me: !p.liked_by_me,
              likes_count: p.likes_count + (p.liked_by_me ? -1 : 1),
            }
          : p,
      ),
    );
    likeFn({ data: { postId } }).catch(() => {
      qc.invalidateQueries({ queryKey: ["following-feed"] });
    });
  };



  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {withAlbum.map((post, i) => (
          <ExploreCard
            key={post.id}
            anchorId={`post-${post.id}`}
            to="/album/$id"
            toParams={{ id: post.album!.discogs_id ?? post.album!.id }}
            cover={post.album!.cover_url ?? null}
            title={post.album!.title}
            artist={post.album!.artist}
            userName={post.author.display_name ?? post.author.username}
            userId={post.author.id}
            userAvatar={post.author.avatar_url ?? null}
            userHref={{ username: post.author.username }}
            isPremium={post.author.is_premium}
            likeCount={post.likes_count}
            liked={post.liked_by_me}
            onToggleLike={() => toggle(post.id)}
            commentCount={post.comments_count}
            onComment={() => setOpenPostId(post.id)}
            onOpen={() => setOpenPostId(post.id)}
            addAlbumData={
              post.album!.discogs_id
                ? {
                    discogsId: post.album!.discogs_id,
                    title: post.album!.title,
                    artist: post.album!.artist,
                    year: post.album!.year,
                    cover: post.album!.cover_url,
                  }
                : undefined
            }
          />
        ))}
      </div>
      {openPostId && (
        <Suspense fallback={null}>
          <PostDetailDrawer
            posts={withAlbum}
            openId={openPostId}
            onOpenChange={setOpenPostId}
            onToggleLike={toggle}
          />
        </Suspense>
      )}
    </>
  );
}


function SpotlightGrid({ posts }: { posts: FeedRow[] }) {
  return <FeedPostsGrid posts={posts} queryKey="spotlight-feed" />;
}

function ForYouFeed() {
  const { user, loading: authLoading } = useCurrentUser();
  const followingFn = useServerFn(listFollowingFeed);
  const q = useQuery<FeedRow[]>({
    queryKey: ["following-feed", user?.id ?? "anon"],
    queryFn: () => followingFn(),
    enabled: !!user,
    retry: false,
    staleTime: 30_000,
  });
  const isUnauthed = !authLoading && !user;
  if (isUnauthed) {
    return (
      <Empty
        title="Faça login para ver seu feed"
        cta={
          <Link to="/auth" className="rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground">
            Entrar
          </Link>
        }
      />
    );
  }
  if (authLoading) return <SkeletonGrid />;

  if (q.isLoading) return <SkeletonGrid />;
  if (!q.data || q.data.length === 0) {
    return (
      <Empty
        title="Seu feed ainda está vazio"
        subtitle="Siga colecionadores ou explore os destaques da comunidade."
        cta={
          <Link
            to="/explore"
            search={{ tab: "spotlight" }}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
          >
            Ver destaques
          </Link>
        }
      />
    );
  }
  return <ForYouGrid posts={q.data} />;
}

function SpotlightTab() {
  const spotlightFn = useServerFn(listSpotlightFeed);
  const q = useQuery<FeedRow[]>({
    queryKey: ["spotlight-feed"],
    queryFn: () => spotlightFn(),
    retry: false,
    staleTime: 60_000,
  });
  const isUnauthed = q.error?.message?.includes("Unauthorized");
  if (isUnauthed) {
    return (
      <Empty
        title="Faça login para ver os destaques"
        cta={
          <Link to="/auth" className="rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground">
            Entrar
          </Link>
        }
      />
    );
  }
  if (q.isLoading) return <SkeletonGrid />;
  if (!q.data || q.data.length === 0) {
    return <Empty title="Nada em destaque ainda" subtitle="Volte em breve." cta={null} />;
  }
  return <SpotlightGrid posts={q.data} />;
}

/** Artistas encontrados na busca: avatar grande que leva para a página dele. */
function ArtistSearchRow({ query }: { query: string }) {
  const artistsFn = useServerFn(searchArtists);
  const q = useQuery({
    queryKey: ["explore-search-artists", query],
    queryFn: () => artistsFn({ data: { q: query } }),
    enabled: query.trim().length > 1,
    staleTime: 10 * 60_000,
  });
  const list = q.data ?? [];
  if (!list.length) return null;

  return (
    <section className="mb-8">
      <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
        Artistas
      </h3>
      <ul className="flex gap-4 overflow-x-auto pb-2">
        {list.map((a) => (
          <li key={a.slug} className="shrink-0">
            <Link
              to="/artistas/$slug"
              params={{ slug: a.slug }}
              search={{ n: a.name } as never}
              className="group flex w-20 flex-col items-center gap-2 text-center"
            >
              <span className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-border bg-surface transition-colors group-hover:border-primary/50">
                {a.imageUrl ? (
                  <img
                    src={thumbUrl(a.imageUrl, 192) ?? undefined}
                    alt={a.name}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Mic2 className="h-7 w-7 text-muted-foreground" />
                )}
              </span>
              <span className="line-clamp-2 text-xs font-semibold leading-tight">{a.name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─────────────────────────────────────────────────────── Discover ───
function DiscoverTab({
  submitted,
  onClearSearch,
  initialSection = "added",
}: {
  submitted: string;
  onClearSearch: () => void;
  initialSection?: "added" | "interest";
}) {
  const searchFn = useServerFn(searchDiscogsPaginated);
  const recentFeedFn = useServerFn(listRecentCollectionFeed);
  const interestFn = useServerFn(listWishlistInterestFeed);
  const [section, setSection] = useState<"added" | "interest">(initialSection);
  const [view, setView] = useState<"grid" | "list">(() => {
    if (typeof window === "undefined") return "grid";
    return window.localStorage.getItem("viny:explore-album-view") === "list" ? "list" : "grid";
  });
  
  const { isAuthenticated } = useCurrentUser();
  const { isPremium } = useIsPremium();
  const isMobile = useIsMobile();

  const publicFeedFn = useServerFn(listRecentCollectionFeedPublic);
  const recentQ = useQuery<FeedRow[]>({
    queryKey: ["recent-collection-feed-public"],
    queryFn: () => publicFeedFn(),
    enabled: !isAuthenticated,
    staleTime: 60_000,
  });


  const infinite = useInfiniteQuery({
    queryKey: ["explore-search", submitted],
    queryFn: ({ pageParam }) =>
      searchFn({ data: { query: submitted, page: pageParam as number, perPage: 24 } }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    enabled: submitted.length > 1,
    staleTime: 1000 * 60 * 30,
  });
  const searchItems: DiscogsSearchItem[] =
    infinite.data?.pages.flatMap((p) => p.items) ?? [];
  const firstPage = infinite.data?.pages[0];
  const corrected =
    firstPage?.correctedFrom && firstPage.query !== firstPage.correctedFrom
      ? firstPage.query
      : null;

  useEffect(() => {
    try {
      window.localStorage.setItem("viny:explore-album-view", view);
    } catch {
      /* preferência apenas local */
    }
  }, [view]);


  return (
    <div className="space-y-6">
      {!submitted && (
        <div className="sticky top-[7.75rem] z-20 -mx-4 border-y border-foreground/10 bg-background/85 px-4 py-2.5 backdrop-blur-2xl lg:top-4 lg:mx-0 lg:rounded-xl lg:border lg:px-3">
          <div className="flex items-center gap-2 lg:justify-between">
            <SegmentedTabs<"added" | "interest">
              layoutId="explore-recent-sections"
              ariaLabel="Tipos de discos recentes"
              fullWidth={isMobile}
              size="sm"
              appearance="native"
              className={cn("h-11 min-w-0", isMobile ? "flex-1" : "w-auto")}
              items={[
                { value: "added", label: "Adicionados" },
                { value: "interest", label: "Interesse" },
              ]}
              value={section}
              onChange={setSection}
            />
            <div className="flex shrink-0 items-center gap-1 rounded-full border border-foreground/10 bg-surface/70 p-1">
              {([
                ["grid", LayoutGrid, "Cards"],
                ["list", List, "Lista"],
              ] as const).map(([mode, Icon, label]) => (
                <Button
                  key={mode}
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Ver em ${label.toLowerCase()}`}
                  aria-pressed={view === mode}
                  title={label}
                  onClick={() => setView(mode)}
                  className={cn(
                    "h-9 w-9 rounded-full",
                    view === mode
                      ? "bg-foreground text-background hover:bg-foreground hover:text-background"
                      : "text-muted-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" />
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
      {!submitted && <CommunityArtistsStrip />}
      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.section
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <SectionHeader title={`Resultados para "${submitted}"`}>
              <button
                onClick={onClearSearch}
                className="text-sm font-semibold text-foreground hover:text-primary transition-colors"
              >
                Limpar busca
              </button>
            </SectionHeader>
            {corrected && (
              <p className="mb-4 text-sm text-muted-foreground">
                Mostrando resultados para <span className="font-semibold text-foreground">{corrected}</span>
              </p>
            )}
            <ArtistSearchRow query={submitted} />
            {searchItems.length === 0 && !infinite.isFetching && (
              <p className="text-sm text-muted-foreground">Nada encontrado.</p>
            )}

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {searchItems.map((item) => (
                <div key={item.id}>
                  <Link to="/album/$id" params={{ id: buildAlbumParam(String(item.discogsId), item.artist, item.title) }} className="block">
                    <div className="aspect-square overflow-hidden rounded-lg bg-surface">
                      {item.cover ? (
                        <img
                          src={item.cover}
                          alt={item.title}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <AlbumCoverSkeleton showMeta={false} />
                      )}
                    </div>
                    <div className="mt-2 truncate text-sm font-semibold">{item.title}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {item.artist}
                      {item.year ? ` · ${item.year}` : ""}
                    </div>
                  </Link>
                  <QuickAddButton
                    variant="full"
                    album={{
                      discogsId: String(item.discogsId),
                      title: item.title,
                      artist: item.artist,
                      year: item.year ?? null,
                      cover: item.cover ?? null,
                    }}
                  />

                </div>
              ))}
              {(infinite.isFetching || infinite.isFetchingNextPage) &&
                Array.from({ length: 12 }).map((_, i) => <AlbumCoverSkeleton key={`sk-${i}`} />)}
            </div>
            {infinite.hasNextPage && (
              <div className="mt-6 text-center">
                <button
                  disabled={infinite.isFetchingNextPage}
                  onClick={() => infinite.fetchNextPage()}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-5 py-2.5 text-sm font-semibold hover:border-primary/50 disabled:opacity-50"
                >
                  {infinite.isFetchingNextPage && <Loader2 className="h-4 w-4 animate-spin" />}
                  Carregar mais
                </button>
              </div>
            )}
          </motion.section>
        ) : (
          <motion.div
            key="discover-default"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-12"
          >
            <section>
              {section === "interest" ? (
                isAuthenticated ? (
                  <PaginatedFeedSection
                    queryKey="wishlist-interest-feed"
                    fetchPage={(offset, limit) => interestFn({ data: { offset, limit } })}
                    view={view}
                  />
                ) : (
                  <Empty
                    title="Faça login para ver os interesses da comunidade"
                    cta={
                      <Link to="/auth" className="rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground">
                        Entrar
                      </Link>
                    }
                  />
                )
              ) : isAuthenticated ? (
                <PaginatedFeedSection
                  queryKey="recent-collection-feed"
                  fetchPage={(offset, limit) => recentFeedFn({ data: { offset, limit } })}
                  view={view}
                />
              ) : recentQ.isLoading ? (
                <SkeletonGrid />
              ) : (recentQ.data?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">Nada por aqui ainda.</p>
              ) : (
                <>
                  <FeedPostsGrid
                    posts={recentQ.data ?? []}
                    queryKey="recent-collection-feed-public"
                    view={view}
                  />
                  <KeepScrollingCard />
                </>
              )}
            </section>

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SectionHeader({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  children?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-end justify-between gap-3 lg:mb-5">
      <div className="flex min-w-0 items-center gap-2">
        {Icon && <Icon className="h-4 w-4 shrink-0 text-primary" />}
        <h2 className="font-display text-lg font-bold lg:text-2xl">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function SkeletonGrid({ count = 18 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{ animationDelay: `${(i % 5) * 110}ms` }}
        >
          <div className="aspect-square w-full rounded-xl bg-white/[0.05]" />
          <div className="mt-2 h-3 w-3/4 rounded-full bg-white/[0.06]" />
          <div className="mt-1.5 h-2.5 w-1/2 rounded-full bg-white/[0.04]" />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────── Shared empty + feed card ───
function Empty({
  title,
  subtitle,
  cta,
}: {
  title: string;
  subtitle?: string;
  cta: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-xl">
      <EmptyState
        title={title}
        description={subtitle}
        icons={[<Disc3 key="d" />, <Heart key="h" />, <Sparkles key="s" />]}
      />
      {cta && <div className="mt-4 flex justify-center">{cta}</div>}
    </div>
  );
}

const TYPE_META: Record<string, { icon: typeof Disc3; label: string }> = {
  added_to_collection: { icon: Disc3, label: "adicionou à coleção" },
  added_to_wishlist: { icon: Heart, label: "adicionou à wishlist" },
  listed_for_sale: { icon: Tag, label: "colocou à venda" },
  listed_for_trade: { icon: Repeat2, label: "abriu para troca" },
};

function FeedCard({ post }: { post: FeedRow }) {
  const qc = useQueryClient();
  const likeFn = useServerFn(togglePostLike);
  const meta = TYPE_META[post.type] ?? { icon: Disc3, label: post.type };
  const TypeIcon = meta.icon;
  const [openComments, setOpenComments] = useState(false);
  const [burst, setBurst] = useState(0);

  const like = useMutation({
    mutationFn: () => likeFn({ data: { postId: post.id } }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ["following-feed"] });
      const prev = qc.getQueryData<FeedRow[]>(["following-feed"]);
      qc.setQueryData<FeedRow[]>(["following-feed"], (old) =>
        (old ?? []).map((p) =>
          p.id === post.id
            ? {
                ...p,
                liked_by_me: !p.liked_by_me,
                likes_count: p.likes_count + (p.liked_by_me ? -1 : 1),
              }
            : p,
        ),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["following-feed"], ctx.prev);
    },
  });

  const handleLove = () => {
    if (!post.liked_by_me) setBurst((b) => b + 1);
    like.mutate();
  };

  return (
    <li className="group relative overflow-hidden rounded-[var(--radius-card)] border border-border/60 bg-card/40 transition-colors hover:border-border">
      {/* Author row */}
      <div className="flex items-center gap-3 px-5 pt-5">
        <Link to="/profile/$username" params={{ username: post.author.username }} className="shrink-0">
          {post.author.avatar_url ? (
            <img loading="lazy" decoding="async"
              src={post.author.avatar_url}
              alt=""
              className="h-9 w-9 rounded-full object-cover"
              style={post.author.is_premium ? { border: "1.5px solid #9231FF", boxSizing: "border-box" } : undefined}
            />
          ) : (
            <div
              className="grid h-9 w-9 place-items-center rounded-full bg-surface text-xs font-bold uppercase text-muted-foreground"
              style={post.author.is_premium ? { border: "1.5px solid #9231FF", boxSizing: "border-box" } : undefined}
            >
              {(post.author.display_name ?? post.author.username).slice(0, 1)}
            </div>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Link
              to="/profile/$username"
              params={{ username: post.author.username }}
              className="truncate text-[13px] font-semibold tracking-tight hover:underline"
            >
              {post.author.display_name ?? post.author.username}
            </Link>
            {post.author.is_premium && <ProBadge size="xs" aria-label="Embaixador" />}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <TypeIcon className="h-3 w-3" />
            <span>{meta.label}</span>
            <span className="opacity-50">·</span>
            <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: ptBR })}</span>
          </div>
        </div>
      </div>

      {/* Album block — editorial */}
      {post.album && (
        <Link
          to="/album/$id"
          params={{ id: buildAlbumParam(post.album.discogs_id ?? post.album.id, post.album.artist, post.album.title) }}
          className="mt-4 flex items-center gap-4 px-5"
        >
          <div className="relative overflow-hidden rounded-xl">
            <img loading="lazy" decoding="async"
              src={post.album.cover_url ?? ""}
              alt={post.album.title}
              className="h-24 w-24 shrink-0 object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-serif text-lg leading-tight">{post.album.title}</div>
            <div className="truncate text-xs text-muted-foreground">
              {post.album.artist}
              {post.album.year ? ` · ${post.album.year}` : ""}
            </div>
            {post.album.price_brl != null && post.type === "listed_for_sale" && (
              <div className="mt-1.5 text-sm font-semibold tabular-nums">
                {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
                  Number(post.album.price_brl),
                )}
              </div>
            )}
          </div>
        </Link>
      )}

      {post.body && (
        <p className="px-5 pt-4 text-[14px] leading-relaxed text-foreground/85"><Linkify text={post.body} /></p>
      )}

      {/* Subtle interaction bar — fades in on hover (always visible on touch) */}
      <div className="mt-4 flex items-center justify-between px-5 pb-4 pt-3 opacity-70 transition-opacity duration-300 group-hover:opacity-100 motion-safe:[@media(hover:none)]:opacity-100">
        <div className="flex items-center gap-4 text-[12px] tabular-nums text-muted-foreground">
          <button
            type="button"
            onClick={handleLove}
            aria-label={post.liked_by_me ? "Remover amei" : "Amei"}
            className="group/heart relative flex items-center gap-1.5 transition-colors hover:text-foreground"
          >
            <span className="relative inline-flex">
              <Heart
                className={cn(
                  "h-[18px] w-[18px] transition-transform duration-300",
                  post.liked_by_me ? "scale-110 text-rose-400" : "group-hover/heart:scale-110",
                )}
                fill={post.liked_by_me ? "currentColor" : "none"}
                strokeWidth={1.5}
              />
              <AnimatePresence>
                {burst > 0 && (
                  <motion.span
                    key={burst}
                    initial={{ scale: 0.6, opacity: 0.9 }}
                    animate={{ scale: 2.2, opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="pointer-events-none absolute inset-0 rounded-full bg-rose-400/30"
                  />
                )}
              </AnimatePresence>
            </span>
            <span className={cn("font-medium", post.liked_by_me && "text-foreground")}>
              {post.likes_count > 0 ? post.likes_count : ""}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setOpenComments(true)}
            aria-label="Comentar"
            className="flex items-center gap-1.5 transition-colors hover:text-foreground"
          >
            <MessageCircle className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <span className="font-medium">{post.comments_count > 0 ? post.comments_count : ""}</span>
          </button>
        </div>
        {post.album?.discogs_id && (
          <QuickAddButton
            size="md"
            album={{
              discogsId: post.album.discogs_id,
              title: post.album.title,
              artist: post.album.artist,
              year: post.album.year,
              cover: post.album.cover_url,
            }}
          />
        )}
      </div>

      <CommentsDrawer
        open={openComments}
        onOpenChange={setOpenComments}
        post={post}
      />
    </li>
  );
}

function CommentsDrawer({
  open,
  onOpenChange,
  post,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  post: FeedRow;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 border-l border-border/60 bg-background p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-border/60 px-6 py-5 text-left">
          <SheetTitle className="font-serif text-2xl leading-tight">Conversa</SheetTitle>
          <SheetDescription className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {post.comments_count} {post.comments_count === 1 ? "comentário" : "comentários"}
          </SheetDescription>
        </SheetHeader>
        <CommentsPanel postId={post.id} />
      </SheetContent>
    </Sheet>
  );
}
